import React from "react";
import { Topic, P, H3, H4, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Tools({ VIXMeter, Sparkline }) {
  return (
    <>
      <Topic id="indicators" title="What are Indicators in Trading?">
        <P>
          When you look at a chart of Nifty, Bank Nifty or Reliance, you&rsquo;ll
          see traders using lines, signals, and patterns. Those are{" "}
          <b>indicators</b> — mathematical tools that help traders understand
          what the market is doing, when to enter or exit, and whether a trend
          is strong, weak, or reversing.
        </P>
        <Callout>
          Indicators don&rsquo;t predict the future — they guide you, like a
          compass for your trading decisions.
        </Callout>

        <H3>Two broad types</H3>
        <H4>1. Trend indicators</H4>
        <P>
          Tell you the overall direction — up, down, or sideways. Example:
          50-day Moving Average. If Nifty is above the 50-day MA → uptrend; below
          → downtrend.
        </P>
        <VisualBox title="EXAMPLE — NIFTY 50-DAY MOVING AVERAGE (ILLUSTRATIVE)">
          <Sparkline data={[18, 19, 20, 22, 24, 23, 26, 28, 27, 30, 32, 31, 34]} />
        </VisualBox>

        <H4>2. Momentum / oscillator indicators</H4>
        <P>Tell you whether the market is overbought or oversold.</P>
        <UL>
          <li>
            <b>RSI</b> — Relative Strength Index, scale 0–100. Above 70 →
            overbought; below 30 → oversold. Example: Reliance RSI at 80 →
            traders expect a correction; at 25 → traders look for a buy.
          </li>
          <li>
            <b>MACD</b> — when the MACD line crosses above the signal line, buy;
            crosses below, sell. Useful on HDFC Bank for trend reversals.
          </li>
          <li>
            <b>Bollinger Bands</b> — price touching upper band suggests
            overbought; lower band suggests oversold. Bands widen during high
            volatility (e.g., results season on Infosys).
          </li>
          <li>
            <b>Volume</b> — price rising with rising volume = strong move; price
            rising on low volume = weak move.
          </li>
          <li>
            <b>VWAP</b> — Volume Weighted Average Price; used by intraday
            traders for fair-price reference.
          </li>
        </UL>

        <H3>Popular indicators used in India</H3>
        <Table
          headers={["Indicator", "Type", "Purpose"]}
          rows={[
            ["RSI", "Momentum", "Overbought / oversold zones"],
            ["Moving Average (SMA / EMA)", "Trend", "Market direction"],
            ["MACD", "Trend + Momentum", "Entry / exit points"],
            ["Bollinger Bands", "Volatility", "Breakouts & range"],
            ["Volume Indicator", "Confirmation", "Trend strength"],
            ["SuperTrend", "Trend", "Easy buy / sell signals"],
            ["VWAP", "Intraday", "Fair-price reference"],
          ]}
        />

        <Callout kind="info">
          <b>Pilot dashboard analogy:</b> speed (RSI), direction (MA), turbulence
          (volatility). A pilot doesn&rsquo;t rely on just one instrument — and
          neither should traders.
        </Callout>
      </Topic>

      <Topic id="india-vix" title="What is India VIX?">
        <P>
          India VIX (Volatility Index) measures the expected volatility — the
          &ldquo;fear&rdquo; — in the Indian stock market over the next 30 days.
          Sometimes Nifty barely moves but option prices jump. That tension is
          what India VIX measures.
        </P>

        <div className="grid md:grid-cols-2 gap-5 items-center my-6">
          <VIXMeter value={13} />
          <div>
            <P>
              Introduced by NSE in 2008, India VIX is calculated from Nifty 50
              option prices. It shows <b>how much</b> traders expect Nifty to
              move — not in which direction. Higher VIX = bigger expected
              swings.
            </P>
          </div>
        </div>

        <H3>How it works in plain English</H3>
        <P>
          India VIX is expressed in % terms. If VIX = 15, traders expect Nifty
          to move ±15% (annualised) over the next year. To get the expected
          30-day move, divide by √12 ≈ 3.46. So 15 / √12 ≈ 4.3% per month — at
          Nifty 26,000, that&rsquo;s roughly ±1,120 points.
        </P>

        <H3>Typical levels &amp; meaning</H3>
        <Table
          headers={["VIX range", "Market condition"]}
          rows={[
            ["10 – 15", "Calm, stable"],
            ["16 – 20", "Mild volatility expected"],
            ["21 – 25", "High volatility, caution"],
            ["26 – 35", "Very high volatility, large moves likely"],
            ["35+", "Panic / extreme uncertainty (rare)"],
          ]}
        />

        <H3>Why it matters</H3>
        <UL>
          <li>
            <b>For option sellers</b> — prefer high VIX (premiums fatter, more
            buffer).
          </li>
          <li>
            <b>For option buyers</b> — prefer low VIX (cheaper entry).
          </li>
          <li>
            <b>For risk managers</b> — rising VIX signals time to hedge.
          </li>
          <li>
            <b>For algo / quant systems</b> — VIX is used as a regime-switch
            input to tune aggressiveness.
          </li>
        </UL>

        <Callout kind="success">
          India VIX is a <b>weather forecast</b> for the market — low VIX is a
          sunny day, high VIX is a storm warning. You don&rsquo;t stop trading,
          you just adjust your strategy.
        </Callout>
      </Topic>
    </>
  );
}
